import {Led3ColorPointer, RgbtColor} from 'pikaparam';
import {LedApi3LightDto} from 'puffco-api-axios-client';

import {colors} from '../../../../../styles';
import {LED3Data, LED3PartyModeData} from '../../../../led3/led3data';
import {
  DeviceModelVariant,
  LightingPattern,
  LumaAnimation,
  MoodType,
  isColorLight,
} from '../../../../types';
import {getLightingPattern} from '../../../../utils';
import {convertRgbArrayToHex} from '../../../../utils/colors';
import {Context, MoodLightEvaluator, fallback} from '../../MoodLightEvaluator';
import {EvaluatedProjector} from '../../MoodLightEvaluator/EvaluatedProjector';
import {Scratchpad, ScratchpadType} from '../../Scratchpad';
import {IUnitClient} from '../UnitClient';
import {
  ColorLight,
  type IUnitLedApi,
  type ReadLightOptions,
  type ReadLightResult,
  type WriteLightOptions,
} from './IUnitLedApi';

const config: Record<
  LightingPattern,
  {moodLight: LedApi3LightDto; context: Context}
> = {
  [LightingPattern.STEADY]: {
    moodLight: LED3Data[MoodType.NO_ANIMATION],
    context: {},
  },
  [LightingPattern.BREATHING]: {
    moodLight: LED3Data[MoodType.BREATHING],
    context: {tempoFrac: 0.5, dynamicInhale: 0},
  },
  [LightingPattern.RISING]: {
    moodLight: LED3Data[MoodType.NO_ANIMATION],
    context: {},
  },
  [LightingPattern.CIRCLING]: {
    moodLight: LED3Data[MoodType.CIRCLING_SLOW],
    context: {tempoFrac: 0.5, dynamicInhale: 0},
  },
  [LightingPattern.PARTY_MODE]: {moodLight: LED3PartyModeData, context: {}},
};

export class UnitLedApiV3 implements IUnitLedApi {
  constructor(private client: IUnitClient) {}

  public async readActiveLedArray(): Promise<number[]> {
    const value =
      await this.client.read<Led3ColorPointer>('activeLedColorApi3');

    return Array.from(RgbtColor.fromRgb(value).bytes);
  }

  public async setPartyMode() {
    // Color is left intentionally empty to satisfy params
    await this.writeLight({
      light: {color: '', pattern: LightingPattern.PARTY_MODE},
      target: 'lantern',
    });
  }

  public async readLight(options: ReadLightOptions): Promise<ReadLightResult> {
    const {target} = options;

    const colorBuffer = await this.client.read<ArrayBuffer>(
      `${target}ColorApi3`,
    );

    const projector = EvaluatedProjector.parse(colorBuffer, {
      fallback,
    });

    const raw = projector.value;

    if (projector.isPartyModeProjector())
      return {
        light: {
          color: colors.defaultColor,
          pattern: LightingPattern.PARTY_MODE,
        },
      };

    if (projector.isSingleColorSolidProjector())
      return {
        light: {
          color: raw.lamp.param.color
            ? convertRgbArrayToHex(raw.lamp.param.color)
            : colors.defaultColor,
        },
      };

    if (projector.isSingleColorPikaProjector())
      return {
        light: {
          color: raw.meta?.userColors?.[0] ?? colors.defaultColor,
          pattern: getLightingPattern(
            raw.lamp.param.anim ?? LumaAnimation.STEADY,
          ),
        },
      };

    if (!Object.keys(raw.meta).length) {
      // TODO: Return default heat cycle factory color corresponding to index
      return {light: {color: colors.defaultColor}};
    }

    const metadata = new Scratchpad(raw.meta as ScratchpadType);

    const {moodLight} = metadata.segregate();

    return {
      light: moodLight ?? {color: colors.defaultColor},
      metadata,
    };
  }

  public async writeLight(options: WriteLightOptions): Promise<void> {
    const {target, light, metadata} = options;

    const property = `${target}ColorApi3` as const;

    const {moodLight, context} = isColorLight(light)
      ? this.getConfigurationForColorLight(light)
      : {
          moodLight: light,
          context: light.projector.meta,
        };

    const initialContext: Context = {
      ...context,
      variant: DeviceModelVariant.LIGHTNING,
    };

    const projector = MoodLightEvaluator.evaluate(moodLight, initialContext);

    if (metadata) projector.addMeta(metadata.value);

    await this.client.write<ArrayBuffer>(property, projector.serialize());
  }

  private getConfigurationForColorLight(light: ColorLight) {
    const {context, ...rest} = config[light.pattern ?? LightingPattern.STEADY];

    return {
      context: {
        ...context,
        ...(light.color !== '' && {userColors: [light.color]}), // Party Mode doesn't have a color
      },
      ...rest,
    };
  }
}
